import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	IconButton,
	MenuItem,
	FormControl,
	InputLabel,
	Typography,
	Switch,
	FormControlLabel,
	useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import type { IScheduleMaintenanceDialogProps } from "../../types/equipment";
import CustomTextField from "../../components/common/Textfield";
import CustomSelect from "../../components/common/Select";
import { tokens } from "../../theme";

interface FormData {
	title: string;
	type: string;
	equipmentIds: string[];
	startDate: string;
	endDate: string;
	isAllDay: boolean;
	assignedTo: string;
	details: string;
	expectedDowntime: string;
	priority: string;
	notificationReminder: string;
}

const ScheduleMaintenanceDialog: React.FC<IScheduleMaintenanceDialogProps> = ({
	open,
	onClose,
	onSubmit,
	equipmentList,
	selectedEquipmentId,
}) => {
	const [formData, setFormData] = useState<FormData>({
		title: "",
		type: "",
		equipmentIds: [],
		startDate: "",
		endDate: "",
		isAllDay: false,
		assignedTo: "",
		details: "",
		expectedDowntime: "",
		priority: "",
		notificationReminder: "",
	});

	useEffect(() => {
		if (selectedEquipmentId) {
			setFormData((prev) => ({
				...prev,
				equipmentIds: [selectedEquipmentId],
			}));
		}
	}, [selectedEquipmentId]);

	const handleChange = (field: string, value: string | boolean | string[]) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = () => {
		if (formData.equipmentIds.length === 0) {
			alert("Please select at least one equipment.");
			return;
		}
		if (!formData.startDate || !formData.endDate) {
			alert("Please provide both start and end dates.");
			return;
		}
		if (new Date(formData.endDate) < new Date(formData.startDate)) {
			alert("End date cannot be before start date.");
			return;
		}
		// Transform formData to match Omit<IMaintenanceSchedule, "id">
		const submitData = {
			...formData,
			equipmentId: formData.equipmentIds[0], // Use first equipment ID
		};
		onSubmit(submitData);
	};

	const maintenanceTypes = [
		"Preventive",
		"Corrective",
		"Predictive",
		"Emergency",
	];
	const assignees = ["John Doe", "Jane Smith", "Mike Johnson", "Sarah Wilson"];
	const priorities = ["Low", "Medium", "High", "Critical"];
	const downtimes = [
		"1 hour",
		"2 hours",
		"3 hours",
		"4 hours",
		"8 hours",
		"1 day",
	];
	const reminders = ["15 minutes", "30 minutes", "1 hour", "2 hours", "1 day"];
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			PaperProps={{
				component: motion.div,
				initial: { opacity: 0, scale: 0.9 },
				animate: { opacity: 1, scale: 1 },
				exit: { opacity: 0, scale: 0.9 },
				transition: { duration: 0.3 },
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography
					variant="h5"
					sx={{ color: colors.primary[300], fontWeight: "bold" }}
				>
					Schedule Maintenance
				</Typography>
				<IconButton onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<Grid container spacing={3}>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomTextField
							fullWidth
							label="Maintenance Title"
							placeholder="Placeholder"
							value={formData.title}
							onChange={(e) => handleChange("title", e.target.value)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomSelect
							labelId="maintenance-type-label"
							label="Maintenance Type"
							value={formData.type}
							onChange={(e) => handleChange("type", e.target.value as string)}
						>
							<MenuItem value="">Placeholder</MenuItem>
							{maintenanceTypes.map((type) => (
								<MenuItem key={type} value={type}>
									{type}
								</MenuItem>
							))}
						</CustomSelect>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomSelect
							labelId="equipment-label"
							label="Equipment"
							multiple
							value={formData.equipmentIds}
							onChange={(e) =>
								handleChange("equipmentIds", e.target.value as string[])
							}
							renderValue={(selected) =>
								(selected as string[])
									.map(
										(id) => equipmentList.find((eq) => eq.id === id)?.name || id
									)
									.join(", ")
							}
							disabled={!!selectedEquipmentId}
						>
							{equipmentList.map((equipment) => (
								<MenuItem key={equipment.id} value={equipment.id}>
									{equipment.name}
								</MenuItem>
							))}
						</CustomSelect>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomTextField
							fullWidth
							label="Start Date & Time"
							type="datetime-local"
							value={formData.startDate}
							onChange={(e) => handleChange("startDate", e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomTextField
							fullWidth
							label="End Date & Time"
							type="datetime-local"
							value={formData.endDate}
							onChange={(e) => handleChange("endDate", e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomSelect
							labelId="assigned-to-label"
							label="Assigned To"
							value={formData.assignedTo}
							onChange={(e) =>
								handleChange("assignedTo", e.target.value as string)
							}
						>
							<MenuItem value="">Placeholder</MenuItem>
							{assignees.map((assignee) => (
								<MenuItem key={assignee} value={assignee}>
									{assignee}
								</MenuItem>
							))}
						</CustomSelect>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<FormControlLabel
							control={
								<Switch
									checked={formData.isAllDay}
									onChange={(e) => handleChange("isAllDay", e.target.checked)}
								/>
							}
							label="All day maintenance"
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<CustomTextField
							fullWidth
							label="Maintenance Details"
							placeholder="Placeholder"
							multiline
							rows={4}
							value={formData.details}
							onChange={(e) => handleChange("details", e.target.value)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomSelect
							labelId="downtime-label"
							label="Expected Downtime (Optional)"
							value={formData.expectedDowntime}
							onChange={(e) =>
								handleChange("expectedDowntime", e.target.value as string)
							}
						>
							<MenuItem value="">Placeholder</MenuItem>
							{downtimes.map((downtime) => (
								<MenuItem key={downtime} value={downtime}>
									{downtime}
								</MenuItem>
							))}
						</CustomSelect>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomSelect
							labelId="priority-label"
							label="Priority (Optional)"
							value={formData.priority}
							onChange={(e) =>
								handleChange("priority", e.target.value as string)
							}
						>
							<MenuItem value="">Placeholder</MenuItem>
							{priorities.map((priority) => (
								<MenuItem key={priority} value={priority}>
									{priority}
								</MenuItem>
							))}
						</CustomSelect>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<CustomSelect
							labelId="reminder-label"
							label="Notification Reminder (Optional)"
							value={formData.notificationReminder}
							onChange={(e) =>
								handleChange("notificationReminder", e.target.value as string)
							}
						>
							<MenuItem value="">Placeholder</MenuItem>
							{reminders.map((reminder) => (
								<MenuItem key={reminder} value={reminder}>
									{reminder}
								</MenuItem>
							))}
						</CustomSelect>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions sx={{ p: 3 }}>
				<Button
					onClick={onClose}
					sx={{
						backgroundColor: colors.primary[800],
						borderRadius: 2,
						color: colors.primary[100],
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					sx={{
						borderRadius: 2,
					}}
				>
					Schedule Maintenance
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ScheduleMaintenanceDialog;
